import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import React from 'react'

import ActionsBox from '@/app/module/campaigns/component/actions/actions-box'
import { useCampaignActionContext } from '@/app/module/campaigns/component/item-provider'
import { SMS_ACTION_DEFAULT_SENDERID } from '@/app/module/campaigns/definitions'
import { CampaignTemplateType, LocalAction, PartFamily, WhatsAppFlow } from '@/app/module/campaigns/types'
import { getActionOptionsForCampaign } from '@/app/module/campaigns/utils/actions/options'
import { ActionContext } from '@/app/module/campaigns/utils/actions/types'
import { unwrapActions, wrapActions } from '@/app/module/campaigns/utils/actions/wrap'

import { ActionProps, ApiProps } from './part-config-props'
import { withCurrentWaFormPz } from './part-response-pz'
import ResponseWhatsappFlowConfigs from './response-whatsapp-flow-configs'

type Props = ActionProps &
  ApiProps & {
    id: string
    item: WhatsAppFlow
    type: CampaignTemplateType

    changeHandler: (item: WhatsAppFlow) => void
  }

const ResponseWhatsappFlow: React.FC<Props> = ({
  countriesTimezones,
  countryCode,
  defaultLanguage,
  files,
  getImageProps,
  getVoiceProps,
  id,
  index,
  item,
  languages,
  orgId,
  personalizationList,
  senderIds,
  timezone,
  timezones,
  token,
  type,
  whatsappSenderIds,
  changeHandler,
}) => {
  const { hasQuotas } = useCampaignActionContext()
  const actions = React.useMemo(() => unwrapActions(item.flow.onAnswer.actions || []), [item.flow.onAnswer.actions])
  const flowAnswerPersonalizationList = React.useMemo(
    () => withCurrentWaFormPz(personalizationList, item.flow, `${index + 1}. ${item.label || 'WhatsApp Form'}`),
    [index, item.flow, item.label, personalizationList],
  )

  const actionOptions = React.useMemo(
    () =>
      getActionOptionsForCampaign({
        campaignType: type,
        partType: PartFamily.WhatsAppFlow,
        hasQuotas,
        actionNamespace: ActionContext.OnAnswer,
      }),
    [hasQuotas, type],
  )

  const handleActionsChange = React.useCallback(
    (actionsList: LocalAction[]) =>
      changeHandler({
        ...item,
        flow: {
          ...item.flow,
          onAnswer: {
            ...item.flow.onAnswer,
            actions: wrapActions(actionsList || []),
          },
        },
      }),
    [item, changeHandler],
  )

  return (
    <div className="whatsapp-flow-response-block">
      <Divider
        style={{
          margin: '1rem -24px',
        }}
      />
      <Typography variant="subtitle1" gutterBottom>
        On form submitted
      </Typography>
      <ActionsBox
        actionOptions={actionOptions}
        actions={actions}
        countriesTimezones={countriesTimezones}
        countryCode={countryCode}
        defaultLanguage={defaultLanguage}
        defaultSenderId={SMS_ACTION_DEFAULT_SENDERID.smsQuestion}
        disableLabel
        files={files}
        getImageProps={getImageProps}
        getVoiceProps={getVoiceProps}
        hasTranscribe={false}
        imageId={`${id}-whatsapp-flow`}
        index={index}
        isCloseDisabled
        languages={languages}
        orgId={orgId}
        personalizationList={flowAnswerPersonalizationList}
        senderIds={senderIds}
        timezone={timezone}
        timezones={timezones}
        token={token}
        transcribeEnabled={false}
        updateContactConfig={{ invalid: false, openResponse: false, question: false }}
        voiceId={`${id}-whatsapp-flow`}
        whatsappSenderIds={whatsappSenderIds}
        onChange={handleActionsChange}
      />
      <ResponseWhatsappFlowConfigs
        changeHandler={changeHandler}
        countriesTimezones={countriesTimezones}
        countryCode={countryCode}
        defaultLanguage={defaultLanguage}
        files={files}
        getImageProps={getImageProps}
        getVoiceProps={getVoiceProps}
        id={id}
        index={index}
        item={item}
        languages={languages}
        orgId={orgId}
        personalizationList={personalizationList}
        senderIds={senderIds}
        timezone={timezone}
        timezones={timezones}
        token={token}
        type={type}
        whatsappSenderIds={whatsappSenderIds}
      />
    </div>
  )
}

export default React.memo(ResponseWhatsappFlow)

export type { Props as ResponseWhatsappFlowProps }
